// The setup list's state on this device: in localStorage under one key, read
// once, written on every change, and followed across tabs. Small enough for
// the shell, since the card it feeds is the first thing a visitor sees.
import { useSyncExternalStore } from 'react';
import { INITIAL, SETUP_ITEMS, parseState, welcomePoints, type SetupItem, type SetupState } from './setup';

export const SETUP_KEY = 'hd:setup';

let state: SetupState | null = null;
const listeners = new Set<() => void>();
/** Items done since the card last showed them: it sparks each one once, wherever it was done (the tour, the wall). In memory only. */
const fresh = new Set<SetupItem>();

function read(): SetupState {
  try {
    return parseState(localStorage.getItem(SETUP_KEY));
  } catch {
    return INITIAL;
  }
}

const notify = () => { for (const l of listeners) l(); };

export const setupStore = {
  get(): SetupState {
    return (state ??= read());
  },
  /** Applies a rule (setup.ts) to the state; writes and tells only when it changed something. */
  update(rule: (s: SetupState) => SetupState): void {
    const before = setupStore.get();
    const next = rule(before);
    if (next === before) return;
    for (const item of SETUP_ITEMS) if (next.marks[item] === 'done' && before.marks[item] !== 'done') fresh.add(item);
    state = next;
    try {
      localStorage.setItem(SETUP_KEY, JSON.stringify(next));
    } catch {
      // A private window: the list holds for this visit.
    }
    notify();
  },
  /** Forgets this device's list: signing out leaves nothing of the last person, and the next starts fresh. */
  reset(): void {
    state = INITIAL;
    fresh.clear();
    try {
      localStorage.removeItem(SETUP_KEY);
    } catch {
      // Nothing was kept.
    }
    notify();
  },
  /** The items done since the last call, each given once. */
  takeFresh(): SetupItem[] {
    const out = [...fresh];
    fresh.clear();
    return out;
  },
  subscribe(listener: () => void): () => void {
    if (listeners.size === 0 && typeof addEventListener === 'function') addEventListener('storage', onStorage);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0 && typeof removeEventListener === 'function') removeEventListener('storage', onStorage);
    };
  },
};

/** Another tab changed it. */
function onStorage(e: StorageEvent): void {
  if (e.key !== SETUP_KEY && e.key !== null) return;
  state = read();
  notify();
}

export function useSetup(): SetupState {
  return useSyncExternalStore(setupStore.subscribe, setupStore.get, setupStore.get);
}

/** The welcome points, for the status card's total. */
export function useWelcomePoints(): number {
  return welcomePoints(useSetup());
}
